import React, { useState, useRef } from 'react';
import { FaEnvelope, FaMapMarkerAlt, FaPhoneAlt, FaPhoneSquareAlt, FaBriefcase, FaCheckCircle } from 'react-icons/fa';
import emailjs from '@emailjs/browser';

const Contact = () => {
  const SERVICE_ID = process.env.REACT_APP_EMAILJS_CONTACT_SERVICE;
  const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_CONTACT_TEMPLATE;
  const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
  const formRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ success: false, message: '' });
  const [phoneError, setPhoneError] = useState('');

  // Phone number validation function
  const validatePhoneNumber = (phone) => {
    // Remove all non-digit characters for validation
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.length >= 10 && cleaned.length <= 15;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate phone number
    const contactNumber = e.target.contactNumber.value.trim();
    if (!validatePhoneNumber(contactNumber)) {
      setPhoneError('Please enter a valid phone number');
      return;
    }

    setPhoneError('');
    setIsSubmitting(true);
    setSubmitStatus({ success: false, message: '' });

    try {
      // Get form data
      const formData = {
        firstName: e.target.firstName.value,
        lastName: e.target.lastName.value,
        email: e.target.email.value,
        contactNumber: contactNumber,
        subject: e.target.subject.value,
        message: e.target.message.value,
      };

      // Send email using EmailJS
      await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          name: `${formData.firstName} ${formData.lastName}`,
          user_email: formData.email,
          reply_to: formData.email, // Required for auto-reply to work
          contactNumber: formData.contactNumber,
          subject: formData.subject,
          message: formData.message,
        },
        PUBLIC_KEY
      );

      // Success - reset form and show message
      formRef.current.reset();
      setPhoneError('');
      setSubmitStatus({ 
        success: true, 
        message: 'Form submitted successfully! We will get back to you soon.' 
      });

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSubmitStatus({ success: false, message: '' });
      }, 5000);

    } catch (error) {
      console.error('EmailJS Error:', error);
      setSubmitStatus({ 
        success: false, 
        message: 'Failed to send message. Please try again later or contact us directly.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const faqData = [
    {
      question: "What is IT consulting, and how can it benefit my business?",
      answer: `IT consulting involves providing expert advice and solutions for businesses to help them optimize their 
  technology infrastructure, improve efficiency, and solve technical problems. By working with an IT 
  consulting firm, your business can gain access to specialized knowledge, save time, reduce costs, 
  and stay up to date with the latest technology trends.`,
    },
    {
      question: "How long does an IT consulting project typically take?",
      answer: `The timeline of an IT consulting project depends on the complexity of the work required. 
  Simple projects, such as setting up a new network, may take a few weeks, while larger projects, 
  such as migrating to a cloud infrastructure or implementing a new enterprise system, 
  can take several months. We provide a detailed timeline for each project to ensure transparency and manage expectations.`,
    },
    {
      question: "How can I get started with your IT consulting services?",
      answer: `Getting started is easy! Simply contact us to schedule a consultation. 
  We will discuss your business goals, assess your IT challenges, and propose 
  a tailored solution that fits your needs. Our team will guide you through every 
  step of the process, from planning to implementation.`,
    },
    {
      question: "How can you help with cybersecurity for my business?",
      answer: `We provide comprehensive cybersecurity services, including vulnerability assessments, 
  penetration testing, firewall setup, encryption, employee training, and ongoing 
  monitoring. Our goal is to protect your sensitive business data and systems from cyber 
  threats like malware, ransomware, and phishing attacks.`,
    },
    {
      question: "How do you ensure the privacy and security of my business data?",
      answer: `We prioritize your data's security by implementing industry-standard encryption, 
  access controls, firewalls, and other security measures to protect it from unauthorized
  access. Our team follows best practices for data protection, and we can assist you 
  in maintaining compliance with regulations such as GDPR, HIPAA, and others.`,
    },
    {
      question: "Can you assist with data recovery and backup solutions?",
      answer: `Yes, we offer data recovery and backup services to ensure that your critical business 
  data is safe. We assess your data storage needs, implement robust backup solutions, 
  and help with disaster recovery planning to minimize downtime in case of data loss.`,
    }
  ];

  return (
    <section className="container py-5 px-3 ">
      <div className="row">
        {/* Left: Contact Form */}
        <div className="col-md-6">
          <div className="bg-light p-4 rounded shadow-sm" style={{ backgroundColor: 'lightSkyBlue' }}>
            <h2 className="fs-2 fw-bold text-dark mb-4 text-start">Contact Us</h2>
            
            {/* Success/Error Message */}
            {submitStatus.message && (
              <div 
                className={`alert ${submitStatus.success ? 'alert-success' : 'alert-danger'} alert-dismissible fade show mb-4`}
                role="alert"
              >
                {submitStatus.success && <FaCheckCircle className="me-2" />}
                {submitStatus.message}
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setSubmitStatus({ success: false, message: '' })}
                  aria-label="Close"
                ></button>
              </div>
            )}

            <form ref={formRef} onSubmit={handleSubmit}>
              <div className="row mb-3 justify-content-start">
                <div className="col-md-6 p-2">
                  <label htmlFor="firstName" className="form-label fw-bold fs-5 text-start d-block">First Name</label>
                  <input type="text" className="form-control form-control-lg" id="firstName" name="firstName" placeholder="First Name" required />
                </div>

                <div className="col-md-6 p-2">
                  <label htmlFor="lastName" className="form-label fw-bold fs-5 text-start d-block">Last Name</label>
                  <input type="text" className="form-control form-control-lg" id="lastName" name="lastName" placeholder="Last Name" required />
                </div>
              </div>

              <div className="mb-3">
                <label htmlFor="email" className="form-label fw-bold fs-5 text-start d-block">Email</label>
                <input type="email" className="form-control form-control-lg" id="email" name="email" placeholder="Email" required />
              </div>

              <div className="mb-3">
                <label htmlFor="contactNumber" className="form-label fw-bold fs-5 text-start d-block">Contact Number</label>
                <input 
                  type="tel" 
                  className={`form-control form-control-lg ${phoneError ? 'is-invalid' : ''}`}
                  id="contactNumber" 
                  name="contactNumber" 
                  placeholder="e.g., +1 (647) 470-8468" 
                  required
                  onChange={() => setPhoneError('')}
                />
                {phoneError && (
                  <div className="invalid-feedback d-block">{phoneError}</div>
                )}
              </div>

              <div className='mb-3'>
                <label htmlFor="subject" className="form-label fw-bold fs-5 text-start d-block">Subject</label>
                <input type="text" className="form-control form-control-lg" id="subject" name="subject" placeholder="Subject" required />
              </div>

              <div className="mb-3">
                <label htmlFor="message" className="form-label fw-bold fs-5 text-start d-block">Message</label>
                <textarea className="form-control form-control-lg" id="message" rows="4" name="message" placeholder="Message" required></textarea>
              </div>

              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </form>
          </div>
        </div>

        {/* Right: Contact Info */}
        <div className="col-md-6 mt-4 mt-md-0 px-5">
          <div className="d-flex flex-column align-items-start text-start">
            {/* Small Contact Us with icon */}
            <div className="d-flex align-items-center mb-1">
              <span className="badge bg-navyblue text-light fw-semibold fs-5 px-3 py-2 shadow-sm mb-2 rounded-pill">
                <FaPhoneAlt className='me-2' /> Contact Us
              </span>

            </div>

            {/* Main Heading */}
            <p className="fs-1 text-dark mb-3" style={{ fontFamily: 'sans-serif' }}>Get in Touch with us</p>

            {/* Subheading */}
            <p className="fs-4 mb-4 text-dark">
              Contact us for tailored IT solutions and cybersecurity services.
            </p>

            {/* Email and Phone */}
            <div className="mb-3">
              <div className="d-flex align-items-center gap-2">
                <FaEnvelope className="me-2" style={{ fontSize: '1.5rem' }} />
                <a
                  href="mailto:info@deepcyberconsulting.com"
                  className="fs-5 text-muted mb-0 text-decoration-none"
                >
                  info@deepcyberconsulting.com
                </a>
              </div>
            </div>
            <div className="mb-3">
              <div className="d-flex align-items-center gap-2">
                <FaPhoneSquareAlt className="me-2" style={{ fontSize: '1.5rem', cursor: 'pointer' }} />
                <a href="tel:+16474708468" className="fs-5 text-muted mb-0 text-decoration-none">
                  +1 (647-470-8468)
                </a>
              </div>
            </div>

            {/* Address and Hours */}
            <div className="mb-3">
              <div className="d-flex align-items-center">
                <FaMapMarkerAlt className="me-3" style={{ fontSize: '1.5rem' }} />
                <p className="fs-5 text-muted mb-0">1898 Dormer Dr, London, ON, N6M 0C1</p>
              </div>

              <div className="d-flex align-items-center mt-5">
                <FaBriefcase className="me-3 fs-4" />
                <p className="fs-4 fw-semibold text-dark mb-0">Our Working Hours</p>
              </div>
              <div className="mt-2">
                <p className="fs-5 text-muted mb-0" style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                  <span className="d-block d-sm-inline">Monday - Friday:</span>{' '}
                  <span className="d-block d-sm-inline">9:00 AM - 6:00 PM</span>
                </p>
              </div>

            </div>
          </div>
          <h2 className="text-center mb-4">FAQ's</h2>
          <div className="accordion w-100 mx-auto" id="faqAccordion">
            {faqData.map((item, index) => {
              const headingId = `heading${index}`;
              const collapseId = `collapse${index}`;
              return (
                <div className="accordion-item mb-3 rounded-3 overflow-hidden " key={index}>
                  <h2 className="accordion-header " id={headingId}>
                    <button
                      className="accordion-button collapsed text-white bg-navyblue"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target={`#${collapseId}`}
                      aria-expanded="false"
                      aria-controls={collapseId}
                    >
                      {item.question}
                    </button>
                  </h2>
                  <div
                    id={collapseId}
                    className="accordion-collapse collapse "
                    aria-labelledby={headingId}
                    data-bs-parent="#faqAccordion"
                  >
                    <div className="accordion-body text-start">
                      {item.answer}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </section>
  );
};

export default Contact;
